import BodyText from 'LuxKit/Typography/BodyText'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import TextLink from 'LuxKit/TextLink'
import Heading from 'LuxKit/Typography/Heading'
import IconButton from 'LuxKit/Button/IconButton'
import ImageGallery from 'LuxKit/Carousel/ImageGallery'
import Modal from 'LuxKit/Modal/Modal'
import MarkdownRender from 'LuxKit/MarkdownRender'
import StarRatingStar from 'components/Common/StarRatingStar'
import FormatCurrency from 'components/Common/FormatCurrency'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Group from 'components/utils/Group'
import { FormattedMessage, useIntl } from 'react-intl'
import { rem } from 'polished'
import styled from 'styled-components'
import { mediaQueryUp } from 'components/utils/breakpoint'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import ModalContext from 'contexts/ModalContext'
import { useCallback, useContext } from 'react'
import type { MouseEventHandler } from 'react'
import LineAngleLeftIcon from 'LuxKit/Icons/line/LineAngleLeftIcon'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import LineMapMarkerIcon from 'LuxKit/Icons/line/LineMapMarkerIcon'
import LineTimesIcon from 'LuxKit/Icons/line/LineTimesIcon'
import type { ExperiencesPassportInclusion } from './experiencesPassportTypes'
import ExperiencesPassportRouteMap from './ExperiencesPassportRouteMap'

const ItemDetailsModal = styled(Modal)`
  [data-testid="experiences-passport-item-details-modal-header-close-button"] {
    display: none;
  }
`

export const EXPERIENCES_PASSPORT_HERO_IMAGE_PARAMS = {
  quality: 'good',
  mobileAspectRatio: '16:9',
  tabletAspectRatio: '16:8',
  desktopAspectRatio: '16:8',
  largeDesktopAspectRatio: '16:8',
  mobileWidth: '100vw',
  tabletWidth: '560px',
  desktopWidth: '560px',
  largeDesktopWidth: '560px',
} as const

const Hero = styled.div`
  position: relative;
  width: 100%;
  overflow: hidden;
`

const CarouselControl = styled(IconButton)`
  position: absolute;
  top: ${rem(12)};
  z-index: 2;
  color: ${props => props.theme.palette.neutral.default.one};
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  border-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  box-shadow: ${props => props.theme.shadow.bottom.small};

  &:hover,
  &:focus,
  &:active {
    color: ${props => props.theme.palette.neutral.default.one};
    background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
    border-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  }

  &.back {
    inset-inline-start: ${rem(12)};
  }

  &.close {
    inset-inline-end: ${rem(12)};
  }
`

const Content = styled.div`
  padding: ${rem(20)};

  ${mediaQueryUp.tablet} {
    padding: ${rem(16)} ${rem(20)} ${rem(20)};
  }
`

const LocationDetail = styled.div`
  display: grid;
  grid-template-columns: ${rem(20)} minmax(0, 1fr);
  column-gap: ${rem(8)};
  align-items: start;

  svg {
    color: ${props => props.theme.palette.neutral.default.one};
  }
`

const RedemptionLinks = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${rem(4)};
  margin-inline-start: ${rem(28)};
`

interface Props {
  inclusion: ExperiencesPassportInclusion
  hideBackButton?: boolean
}

export function getInclusionImages(inclusion: ExperiencesPassportInclusion): Array<App.Image> {
  // Real Experiences Passport data can use App.Image.id so ImageCarousel loads it via the LE image CDN.
  // External mocked images must omit id; when id is present, ImageCarousel treats the image as internal.
  if (inclusion.images?.length) return inclusion.images
  if (inclusion.image) return [inclusion.image]
  if (inclusion.imageUrl) return [{ url: inclusion.imageUrl, title: inclusion.title }]
  return []
}

function ExperiencesPassportItemDetailsModal({ inclusion, hideBackButton = false }: Props) {
  const intl = useIntl()
  const modalContext = useModalElementContext()
  const showModal = useContext(ModalContext)
  const images = getInclusionImages(inclusion)
  const closeItemModal = useCallback<MouseEventHandler<HTMLElement>>((event) => {
    event.stopPropagation()
    modalContext?.resolve()
  }, [modalContext])
  const openInformationModal = useCallback((title: string, content: string) => {
    showModal(<Modal title={title}>
      <MarkdownRender content={content} type="compact" />
    </Modal>)
  }, [showModal])
  const openHowToRedeemModal = useCallback(() => {
    if (!inclusion.howToRedeem) return

    openInformationModal(intl.formatMessage({
      id: 'yx--eHRptJPotv-OGMp-',
      defaultMessage: 'How to redeem?',
      description: 'Title for the Experiences Passport how to redeem modal',
    }), inclusion.howToRedeem)
  }, [inclusion.howToRedeem, intl, openInformationModal])
  const openTermsAndConditionsModal = useCallback(() => {
    if (!inclusion.offerConditions) return

    openInformationModal(intl.formatMessage({
      id: 'vD_hm_3DXTtRXoaYI0UJ',
      defaultMessage: 'T&Cs apply',
      description: 'Title for the Experiences Passport terms and conditions modal',
    }), inclusion.offerConditions)
  }, [inclusion.offerConditions, intl, openInformationModal])

  return <ItemDetailsModal
    data-testid="experiences-passport-item-details-modal"
    title=""
    dismissible
    onClose={closeItemModal}
    flush
    flushVerticalTop
    isHeaderOverlay={!!images.length}
    size="S"
  >
    <Hero>
      {!!images.length && <ImageGallery
        images={images}
        imageParams={EXPERIENCES_PASSPORT_HERO_IMAGE_PARAMS}
        arrowBehaviour="interaction-visible"
        showImageTitles={false}
      />}
      {!hideBackButton && <CarouselControl
        aria-label={intl.formatMessage({
          id: '_WmmEKsrsGXPj9VBwbIl',
          defaultMessage: 'Back',
          description: 'Button label to remove applied content tag filter and go back on search page',
        })}
        className="back"
        kind="secondary"
        variant="default"
        shape="circle"
        size="medium"
        onClick={closeItemModal}
      >
        <LineAngleLeftIcon />
      </CarouselControl>}
      <CarouselControl
        aria-label={intl.formatMessage({
          id: 'WYXh_8yLMKajGJmG4XY0',
          defaultMessage: 'close',
          description: 'Aria label for close button in modal header dialog window',
        })}
        className="close"
        kind="secondary"
        variant="default"
        shape="circle"
        size="medium"
        onClick={closeItemModal}
      >
        <LineTimesIcon />
      </CarouselControl>
    </Hero>
    <Content>
      <VerticalSpacer gap={24}>
        <VerticalSpacer gap={4}>
          <Heading variant="heading3">{inclusion.title}</Heading>
          {!!inclusion.rating && <Group direction="horizontal" gap={4} verticalAlign="center">
            <StarRatingStar kind="filled" size="XS" />
            <BodyText variant="small">
              <FormattedMessage
                id="bfByel_k25Q8ntz_NCuw"
                defaultMessage="{rating} ({reviewsText})"
                description="Rating and review count for an Experiences Passport inclusion in the list modal"
                values={{
                  rating: inclusion.rating,
                  reviewsText: inclusion.reviewsText,
                }}
              />
            </BodyText>
          </Group>}
          {!!inclusion.description && <BodyText variant="medium" colour="light-foreground-alt">{inclusion.description}</BodyText>}
          {!!inclusion.value && <BodyText variant="medium" weight="bold" colour="highlight-primary-dark">
            <FormattedMessage
              id="pojZNR-VAugwT6mDuIHl"
              defaultMessage="Valued up to {value}"
              description="Experiences Passport inclusion monetary value shown in the item detail modal"
              values={{
                value: <FormatCurrency
                  value={inclusion.value}
                  currency={inclusion.valueCurrencyCode}
                  format="roundedDollar"
                />,
              }}
            />
          </BodyText>}
        </VerticalSpacer>

        <VerticalSpacer gap={12}>
          {!!inclusion.locationText && <LocationDetail>
            <LineMapMarkerIcon size="S" />
            <Group direction="vertical" gap={2}>
              <BodyText variant="medium" weight="bold">{inclusion.locationText}</BodyText>
              {!!inclusion.locationAddressText && <BodyText variant="medium">
                {inclusion.locationAddressText}
              </BodyText>}
              {!!inclusion.locationDistanceText && <BodyText variant="medium" weight="bold" colour="highlight-primary-dark">
                {inclusion.locationDistanceText}
              </BodyText>}
            </Group>
          </LocationDetail>}
          <BodyTextBlock variant="medium" startIcon={<LineInfoCircleIcon />}>
            <FormattedMessage
              id="-fSVVgeKhMagqnFxOeqA"
              defaultMessage="Redeem during your stay."
              description="Default redemption instructions for an Experiences Passport inclusion"
            />
          </BodyTextBlock>
          {(!!inclusion.howToRedeem || !!inclusion.offerConditions) && <RedemptionLinks>
            {!!inclusion.howToRedeem &&
              <TextLink size="medium" onClick={openHowToRedeemModal}>
                <FormattedMessage
                  id="l7n9JSUfh9Q-2qwrPieL"
                  defaultMessage="How to redeem?"
                  description="Link to open the Experiences Passport how to redeem modal"
                />
              </TextLink>}
            {!!inclusion.offerConditions && <TextLink size="medium" onClick={openTermsAndConditionsModal}>
              <FormattedMessage
                id="bKARDo8ar_ZyS59OMTFP"
                defaultMessage="T&Cs apply"
                description="Link to open the Experiences Passport terms and conditions modal"
              />
            </TextLink>}
          </RedemptionLinks>}
        </VerticalSpacer>

        <ExperiencesPassportRouteMap inclusion={inclusion} />
      </VerticalSpacer>
    </Content>
  </ItemDetailsModal>
}

export default ExperiencesPassportItemDetailsModal
